import _ from "lodash";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { AppCard } from "@lib/components/AppComponents";
import { useIntl } from "react-intl";
import { ActionButton } from "../../Buttons";
import { Empty, Skeleton, Typography } from "antd";
import { CurrencyEnum, PaginationType, UseQueryProps } from "@lib/types";
import { AILog } from "@lib/types/AILog";
import { useMemo } from "react";
import { useTheme } from "styled-components";
import { randomColor } from "@lib/helpers/ColorHelper";
import { currencySymbol, formatCurrency } from "@lib/helpers/StringHelper";
import { StyledFlex } from "../../Table/index.styled";

type Props = {
  query?: UseQueryProps<PaginationType<AILog>>;
  extra?: string | JSX.Element;
  navigate?: () => void;
  loading?: boolean;
  refetch?: () => void;
};

const AIModelCustomerUsageChart = (props: Props) => {
  const { query, extra, navigate, loading, refetch } = props;
  const { mode } = useTheme();
  const { data, isLoading, isFetching } = query || {};
  const { messages } = useIntl();

  const chartData = data?.data?.data || [];

  // const modelIds = _.uniq(chartData?.map((item) => item.modelId));
  const customerNames = _.uniq(chartData?.map((item) => item.customer?.name));

  const labelText = (customerName: string) => {
    return (
      formatCurrency(
        chartData
          .filter((item) => item.customer?.name === customerName)
          .reduce((acc, item) => acc + item.cost, 0),
      ) +
      " " +
      currencySymbol(CurrencyEnum.USD)
    );
  };

  const chartSeries: ApexOptions["series"] = useMemo(() => {
    const series: ApexOptions["series"] = customerNames.map((customerName) =>
      chartData
        .filter((item) => item.customer?.name === customerName)
        .reduce((acc, item) => acc + item.cost, 0),
    );

    return series;
  }, [chartData]);

  const chartOptions = useMemo(() => {
    return {
      theme: {
        mode: mode,
      },
      chart: {
        type: "pie",
        height: 400,
        width: 400,
      },
      labels: customerNames,
      colors: customerNames.map((v, i) => {
        return randomColor(i);
      }),
      tooltip: {
        y: {
          formatter: (val: number, opt: any) => {
            return labelText(customerNames[opt.dataPointIndex]);
          },
        },
        shared: true,
        intersect: false,
      },
      legend: {
        formatter: (val: string) => {
          return `${val} : ${labelText(val)}`;
        },
      },
    } as ApexOptions;
  }, [mode, messages, customerNames, labelText]);

  return (
    <AppCard
      title={messages["common.aiModelCustomerUsageChart"] as string}
      extra={
        <StyledFlex style={{ gap: 10 }}>
          {extra}
          <>
            {navigate && (
              <Typography.Link onClick={() => navigate()}>
                {messages["common.viewAll"] as string}
              </Typography.Link>
            )}
          </>
          {refetch && (
            <ActionButton
              type="refresh"
              size="small"
              onClick={() => refetch()}
              disabled={loading}
              loading={loading}
            />
          )}
        </StyledFlex>
      }
    >
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {chartData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type={chartOptions.chart?.type}
              height={chartOptions.chart?.height || 400}
            />
          )}
        </>
      )}
    </AppCard>
  );
};

export default AIModelCustomerUsageChart;
